import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const complete = (data) => typeof data?.executive_summary === 'string'
  && data.executive_summary.length >= 100
  && ['functional_requirements', 'project_plan', 'risk_assessment', 'staffing_plan', 'roles_responsibilities']
    .every(key => Array.isArray(data[key]) && data[key].length > 0)
  && data.proposed_technical_solution?.key_components?.length > 0;

const wait = (signal) => new Promise((resolve, reject) => {
  const cancel = () => { clearTimeout(timer); reject(new axios.CanceledError()); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 4000);
  signal.addEventListener('abort', cancel, { once: true });
});

export const useProposal = (userId, active) => {
  const [state, setState] = useState({ status: 'idle', proposal: null, error: '' });
  const request = useRef(null);
  const stateUser = useRef(userId);

  useEffect(() => {
    stateUser.current = userId;
    setState({ status: 'idle', proposal: null, error: '' });
    return () => { request.current?.abort(); request.current = null; };
  }, [userId]);

  const fetchOrGenerateProposal = useCallback(async (retry = false) => {
    if (!userId || request.current) return;
    const controller = new AbortController();
    request.current = controller;
    const config = { signal: controller.signal, timeout: 20000 };
    setState({ status: 'loading', proposal: null, error: '' });
    try {
      let response;
      try {
        response = await axios.get(`${API}/proposal/${userId}`, config);
      } catch (err) {
        const status = err.response?.status;
        const incomplete = status === 409 && err.response?.data?.detail?.code === 'incomplete_proposal';
        if (!(status === 404 || incomplete || (retry && status === 503))) throw err;
        // Exactly one start/recovery request. All following requests only read status.
        response = await axios.post(`${API}/proposal/generate/${userId}`, null,
          { ...config, params: { retry: retry === true } });
      }
      const deadline = Date.now() + 300000;
      while (response.status === 202 && Date.now() < deadline) {
        await wait(controller.signal);
        response = await axios.get(`${API}/proposal/${userId}`, config);
      }
      if (response.status === 202) throw new Error('The proposal is still processing. Check again shortly; your assessment is saved.');
      if (!complete(response.data)) throw new Error('The proposal is incomplete and cannot be displayed as a finished report. Please try again.');
      if (!controller.signal.aborted && stateUser.current === userId) {
        setState({ status: 'ready', proposal: response.data, error: '' });
      }
    } catch (err) {
      if (axios.isCancel(err) || controller.signal.aborted) return;
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message;
      if (stateUser.current === userId) setState({ status: 'error', proposal: null,
        error: message || err.message || 'The proposal could not be completed. Your assessment is saved.' });
    } finally {
      if (request.current === controller) request.current = null;
    }
  }, [userId]);

  useEffect(() => {
    if (active && userId && state.status === 'idle') fetchOrGenerateProposal();
  }, [active, userId, state.status, fetchOrGenerateProposal]);

  return { proposal: state.proposal, proposalLoading: state.status === 'loading',
    proposalError: state.error, fetchOrGenerateProposal };
};